import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/dates";

/**
 * The invoice for an accepted quote, on the FixyBee's own quote (PLANES M7).
 *
 * One button: "Send invoice" the first time, "Resend" after — the server
 * issues the invoice (its number is the FixyBee's own 1, 2, 3...) on the first
 * send and mails the same PDF on every later one. The client receives it by
 * email from FixyDany; their address never comes back to this screen.
 * Download is for the FixyBee's records.
 *
 * Rendered only by the quote's FixyBee, only on an accepted quote — the
 * server refuses anything else anyway (403 / 409), and a button that exists
 * only to be refused is a worse answer than no button.
 */
export default function InvoicePanel({ quoteId }) {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/invoices/quote/${quoteId}`)
      .then((r) => { if (live) setInvoice(r.data?.invoice || null); })
      .catch(() => {})
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [quoteId]);

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const { data } = await api.post(`/invoices/quote/${quoteId}/send`);
      setInvoice(data.invoice);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setProblem(code === "invoice_copy_pending" ? "unavailable" : "failed");
      // A 502 still created the invoice: re-read so the number shows.
      api.get(`/invoices/quote/${quoteId}`)
        .then((r) => setInvoice(r.data?.invoice || null)).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const { data } = await api.get(`/invoices/${invoice.invoice_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.number_label}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setProblem("failed");
    }
  };

  if (!loaded) return null;
  const sentOn = formatDate(invoice?.sent_at) || null;

  return (
    <div className="mt-4 pt-4 border-t border-sand" data-testid="invoice-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-display font-bold text-worker flex items-center gap-2">
            <FileText size={16} className="text-hive-ink" />
            {invoice ? t("invoice.numbered", { number: invoice.number_label }) : t("invoice.title")}
          </p>
          <p className="text-xs text-smoke mt-0.5" data-testid="invoice-state">
            {sentOn ? t("invoice.sentOn", { date: sentOn }) : t("invoice.hint")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={send} disabled={busy}
                  className={invoice?.sent_at ? "fx-btn fx-btn--secondary text-sm" : "fx-btn fx-btn--primary text-sm"}
                  data-testid="invoice-send">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {invoice?.sent_at ? t("invoice.resend") : t("invoice.send")}
          </button>
          {invoice && (
            <button type="button" onClick={download} className="fx-btn fx-btn--ghost text-sm"
                    data-testid="invoice-download">
              <Download size={14} /> {t("invoice.download")}
            </button>
          )}
        </div>
      </div>
      {problem && (
        <p className="mt-2 text-xs text-danger" data-testid="invoice-problem">
          {problem === "unavailable" ? t("invoice.unavailable") : t("invoice.failed")}
        </p>
      )}
    </div>
  );
}
